import type { PropertySelectionRow } from "../../types";
import { normalizeLocation } from "../index";
import { getPropertyRowPosition } from "./propertyRowPosition";

/** 地番・家屋番号検索の検索元の物件（ピン・物件リストの行）と、その座標 */
export interface KaokuSearchSource {
  prefName: string;
  location: string;
  lat: number;
  lng: number;
}

/** 検索元がすべて同じ地点か（空なら false） */
const isSinglePosition = (sources: readonly KaokuSearchSource[]): boolean => {
  const [first] = sources;
  return (
    !!first &&
    sources.every(
      (source) => source.lat === first.lat && source.lng === first.lng
    )
  );
};

/**
 * 地番・家屋番号検索で物件リストに足す行に、検索元の物件の座標を持たせる。
 *
 * 検索結果の行は所在・地番（家屋番号）しか持たないが、物件リストの保存（JSON）は座標が必須のため。
 * 建物は検索元の土地の上にあり、土地は検索元の建物の下にあるので、検索元の座標を使う。
 *
 * 検索の入力は検索元から作り、モーダルでは選ぶだけで書き換えられないので、検索元が1つの地点に
 * まとまる時（ピンのポップアップから検索した時など）は、どの結果にもその座標を付ける。
 * 検索元が複数の地点に分かれる時（物件リストからまとめて検索した時）は、都道府県・所在が同じ検索元が
 * 1つの地点に決まる結果だけに付ける（BE は所在の表記を整えて返すことがあり、合わない結果には付けない）。
 * もともと座標を持つ行はそのまま返す
 */
export const withKaokuSearchSourcePosition = (
  row: PropertySelectionRow,
  sources: readonly KaokuSearchSource[]
): PropertySelectionRow => {
  if (getPropertyRowPosition(row)) return row;

  const location = normalizeLocation(row.location);
  const candidates = isSinglePosition(sources)
    ? sources
    : sources.filter(
        (source) =>
          source.prefName === row.prefName &&
          normalizeLocation(source.location) === location
      );
  const [first] = candidates;
  if (!first || !isSinglePosition(candidates)) return row;

  return {
    ...row,
    rawData: {
      prefName: row.prefName,
      cityName: "",
      ooazaName: null,
      azaName: null,
      chiban: row.chibanKaokuNumber,
      addressChiban: row.chibanKaokuNumber,
      polygon: undefined,
      lat: first.lat,
      lng: first.lng,
      address: "",
      area: row.area,
    },
  };
};
